import React from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hook";
import { createPrincipalWithFormData, updatePrincipalWithFormData, fetchPrincipalList } from "../../../features/principal/PrincipalSlice";
import { RootState } from "../../../store/store";
import { Loader2 } from "lucide-react";
import PrincipalFormHeader from "./components/PrincipalFormHeader";
import ErrorDisplay from "./components/ErrorDisplay";
import { PrincipalFormData } from "./types";
import { useLobRegion } from "../../../hooks/useLobRegion";

interface ReviewPrincipalPageProps {
  formData: PrincipalFormData;
  isEditMode: boolean;
  principalId?: string;
  onBack: () => void;
  onSubmitSuccess: () => void;
}

const ReviewPrincipalPage: React.FC<ReviewPrincipalPageProps> = ({
  formData,
  isEditMode,
  principalId,
  onBack,
  onSubmitSuccess,
}) => {
  const region = useLobRegion();
  const dispatch = useAppDispatch();
  const { principalLoading, principalError } = useAppSelector((state: RootState) => state.principal);

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (value instanceof File) return value.name;
    return String(value);
  };

  const getLogoPreview = (logo: File): string => {
    return URL.createObjectURL(logo);
  };

  const formatCommissionSlabs = (slabs: { range: string; percentage: string }[]): string => {
    if (!slabs || slabs.length === 0) return "—";
    return slabs
      .filter((slab) => slab.range && slab.percentage)
      .map((slab) => `${slab.range}: ${slab.percentage}%`)
      .join(", ");
  };

  const handleSubmit = async () => {
    try {
      // Prepare commission slabs
      const commissionSlabs: Record<string, number> | null = (() => {
        const slabs: Record<string, number> = {};
        for (const slab of formData.commissionSlabs) {
          if (slab.range && slab.percentage) {
            const percentage = parseFloat(slab.percentage);
            if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
              slabs[slab.range] = percentage;
            }
          }
        }
        return Object.keys(slabs).length > 0 ? slabs : null;
      })();

      // Prepare principalBanks - only include if at least one bank has data
      // Include principalBankId for existing banks, omit for new ones
      const principalBanks = formData.principalBanks.length > 0
        ? formData.principalBanks
            .filter((bank) => bank.name || bank.accountNo)
            .map((bank) => {
              const bankData: any = {
                name: bank.name,
                accountHolderName: bank.accountHolderName,
                accountNo: bank.accountNo,
                ifscCode: bank.ifscCode,
                bankCity: bank.bankCity,
              };
              // Only include principalBankId if it exists (for updates)
              if (bank.principalBankId) {
                bankData.principalBankId = bank.principalBankId;
              }
              return bankData;
            })
        : [];

      // Prepare contactInformation - only include if at least email or phone is provided
      const contactInformation = (formData.contactInformation.email || formData.contactInformation.phone)
        ? {
            email: formData.contactInformation.email,
            phone: formData.contactInformation.phone,
            contactPerson: formData.contactInformation.contactPerson,
            address: {
              city: formData.contactInformation.address.city,
              state: formData.contactInformation.address.state,
              street: formData.contactInformation.address.street,
              country: formData.contactInformation.address.country,
              zipCode: formData.contactInformation.address.zipCode,
            },
          }
        : null;

      // Always use FormData for both create and update
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name.trim());
      formDataToSend.append("principalCode", formData.principalCode.trim());
      formDataToSend.append("shortName", formData.shortName.trim());
      if (commissionSlabs) {
        formDataToSend.append("commissionSlabs", JSON.stringify(commissionSlabs));
      }
      if (formData.logoUrl) {
        formDataToSend.append("logo", formData.logoUrl);
      }
      formDataToSend.append("commissionPercentage", formData.commissionPercentage || "0");
      formDataToSend.append("commissionFixedAmount", formData.commissionFixedAmount || "0.00");
      formDataToSend.append("voucherType", formData.voucherType || "");
      formDataToSend.append("country", formData.country);
      formDataToSend.append("isDistributionOnly", String(formData.isDistributionOnly));
      formDataToSend.append("commissionType", formData.commissionType);
      formDataToSend.append("paymentTerm", formData.paymentTerm.trim() || "");
      if (formData.contractDocumentUrl) {
        formDataToSend.append("contractDocument", formData.contractDocumentUrl);
      }
      formDataToSend.append("principalConversionRate", formData.principalConversionRate || "");
      formDataToSend.append("useLogo", String(formData.useLogo || false));
      formDataToSend.append("redemptionNotification", String(formData.redemptionNotification || false));
      formDataToSend.append("redemptionDetails", formData.redemptionDetails.trim() || "");
      formDataToSend.append("codeValidityBalanceCheck", String(formData.codeValidityBalanceCheck || false));
      formDataToSend.append("codeActivationFeeAmount", formData.codeActivationFeeAmount || "");
      formDataToSend.append("codeActivationFeeCurrency", formData.codeActivationFeeCurrency || "");
      // Stringify JSON fields for backend parsing
      if (principalBanks && principalBanks.length > 0) {
        formDataToSend.append("principalBanks", JSON.stringify(principalBanks));
      }
      if (contactInformation) {
        formDataToSend.append("contactInformation", JSON.stringify(contactInformation));
      }
      if (formData.whiteListedIps && formData.whiteListedIps.length > 0) {
        formDataToSend.append("whiteListedIps", JSON.stringify(formData.whiteListedIps));
      }
      formDataToSend.append("merchantId", formData.merchantId.trim() || "");
      if (formData.addSelfDistributor && formData.distributorId) {
        formDataToSend.append("distributorId", formData.distributorId);
      }

      if (isEditMode && principalId) {
        // In edit mode, do NOT send account information (email/password)
        await dispatch(updatePrincipalWithFormData({ principalId, formData: formDataToSend })).unwrap();
      } else {
        // In create mode, include account information
        formDataToSend.append("email", formData.email.trim());
        formDataToSend.append("password", formData.password);
        await dispatch(createPrincipalWithFormData(formDataToSend)).unwrap();
      }
      await dispatch(fetchPrincipalList());
      onSubmitSuccess();
    } catch (error) {
      const errorElement = document.getElementById("review_PrincipalError");
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  return (
    <div className="bg-white h-full">
      <PrincipalFormHeader title={isEditMode ? "Review Principal Changes" : "Review Principal Details"} onBack={onBack} />

      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 space-y-8 pb-8">
        <ErrorDisplay error={principalError} id="review_PrincipalError" />

        {/* Basic Information */}
        <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
          <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Principal Name</label>
              <p className="text-sm text-gray-900">{formatValue(formData.name)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Principal Code</label>
              <p className="text-sm text-gray-900">{formatValue(formData.principalCode)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Short Name</label>
              <p className="text-sm text-gray-900">{formatValue(formData.shortName)}</p>
            </div>
            {region.indiaFields && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Merchant ID</label>
              <p className="text-sm text-gray-900">{formatValue(formData.merchantId)}</p>
            </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Logo</label>
              {formData.logoUrl ? (
                <div className="flex items-center gap-2">
                  <img src={getLogoPreview(formData.logoUrl)} alt="Logo preview" className="w-20 h-20 object-contain border border-gray-300 rounded" />
                  <span className="text-sm text-gray-900">{formData.logoUrl.name}</span>
                </div>
              ) : (
                <p className="text-sm text-gray-900">—</p>
              )}
            </div>
          </div>
        </div>

        {/* Commission Information */}
        <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
          <h2 className="text-lg font-semibold mb-4">Commission Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Commission Type</label>
              <p className="text-sm text-gray-900 capitalize">{formatValue(formData.commissionType)}</p>
            </div>
            {(formData.commissionType === "percentage" || formData.commissionType === "precentage") && formData.commissionPercentage && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Commission Percentage</label>
                <p className="text-sm text-gray-900">{formatValue(formData.commissionPercentage)}%</p>
              </div>
            )}
            {formData.commissionType === "fixed" && formData.commissionFixedAmount && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Commission Fixed Amount</label>
                <p className="text-sm text-gray-900">{formatValue(formData.commissionFixedAmount)}</p>
              </div>
            )}
            {formData.commissionSlabs.length > 0 && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-500 mb-1">Commission Slabs</label>
                <p className="text-sm text-gray-900">{formatCommissionSlabs(formData.commissionSlabs)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Voucher & Location Information */}
        <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
          <h2 className="text-lg font-semibold mb-4">Voucher & Location Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {region.indiaFields && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Voucher Type</label>
              <p className="text-sm text-gray-900">{formatValue(formData.voucherType)}</p>
            </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Country</label>
              <p className="text-sm text-gray-900">{formatValue(formData.country)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Payment Term</label>
              <p className="text-sm text-gray-900">{formatValue(formData.paymentTerm)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Contract Document URL</label>
              {formData.contractDocumentUrl ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-900">{formData.contractDocumentUrl.name}</span>
                  <span className="text-xs text-gray-500">({(formData.contractDocumentUrl.size / 1024 / 1024).toFixed(2)} MB)</span>
                </div>
              ) : (
                <p className="text-sm text-gray-900">—</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Distribution Only</label>
              <p className="text-sm text-gray-900">{formatValue(formData.isDistributionOnly)}</p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        {(formData.contactInformation.email || formData.contactInformation.phone || formData.contactInformation.contactPerson) && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
            <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {formData.contactInformation.email && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Contact Email</label>
                  <p className="text-sm text-gray-900">{formatValue(formData.contactInformation.email)}</p>
                </div>
              )}
              {formData.contactInformation.phone && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Contact Phone</label>
                  <p className="text-sm text-gray-900">{formatValue(formData.contactInformation.phone)}</p>
                </div>
              )}
              {formData.contactInformation.contactPerson && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Contact Person</label>
                  <p className="text-sm text-gray-900">{formatValue(formData.contactInformation.contactPerson)}</p>
                </div>
              )}
              {(formData.contactInformation.address.street || formData.contactInformation.address.city) && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Address</label>
                  <p className="text-sm text-gray-900">
                    {[
                      formData.contactInformation.address.street,
                      formData.contactInformation.address.city,
                      formData.contactInformation.address.state,
                      formData.contactInformation.address.country,
                      formData.contactInformation.address.zipCode,
                    ].filter(Boolean).join(", ")}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Whitelisted IPs */}
        {formData.whiteListedIps.length > 0 && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
            <h2 className="text-lg font-semibold mb-4">Whitelisted IPs</h2>
            <div className="flex flex-wrap gap-2">
              {formData.whiteListedIps.map((ip, index) => (
                <span key={index} className="bg-gray-100 rounded-full px-3 py-1 text-sm">
                  {ip}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Additional Settings */}
        <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
          <h2 className="text-lg font-semibold mb-4">Additional Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Principal Conversion Rate</label>
              <p className="text-sm text-gray-900">{formatValue(formData.principalConversionRate)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Code Activation Fee Amount</label>
              <p className="text-sm text-gray-900">{formatValue(formData.codeActivationFeeAmount)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Code Activation Fee Currency</label>
              <p className="text-sm text-gray-900">{formatValue(formData.codeActivationFeeCurrency)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Use Logo</label>
              <p className="text-sm text-gray-900">{formatValue(formData.useLogo)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Redemption Notification</label>
              <p className="text-sm text-gray-900">{formatValue(formData.redemptionNotification)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Code Validity Balance Check</label>
              <p className="text-sm text-gray-900">{formatValue(formData.codeValidityBalanceCheck)}</p>
            </div>
            {formData.redemptionDetails && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-500 mb-1">Redemption Details</label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{formatValue(formData.redemptionDetails)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Bank Details */}
        {formData.principalBanks.length > 0 && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
            <h2 className="text-lg font-semibold mb-4">Bank Details</h2>
            <div className="space-y-4">
              {formData.principalBanks.map((bank, index) => (
                <div key={bank.id} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-md font-medium text-gray-900 mb-3">Bank Account {index + 1}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {bank.name && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Bank Name</label>
                        <p className="text-sm text-gray-900">{formatValue(bank.name)}</p>
                      </div>
                    )}
                    {bank.accountHolderName && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Account Holder Name</label>
                        <p className="text-sm text-gray-900">{formatValue(bank.accountHolderName)}</p>
                      </div>
                    )}
                    {bank.accountNo && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Account Number</label>
                        <p className="text-sm text-gray-900">{formatValue(bank.accountNo)}</p>
                      </div>
                    )}
                    {bank.ifscCode && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">IFSC Code</label>
                        <p className="text-sm text-gray-900">{formatValue(bank.ifscCode)}</p>
                      </div>
                    )}
                    {bank.bankCity && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Bank City</label>
                        <p className="text-sm text-gray-900">{formatValue(bank.bankCity)}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Account Information */}
        <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
          <h2 className="text-lg font-semibold mb-4">Account Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
              <p className="text-sm text-gray-900">{formatValue(formData.email)}</p>
            </div>
            {!isEditMode && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Password</label>
                <p className="text-sm text-gray-900">••••••••</p>
              </div>
            )}
          </div>
        </div>

        {/* Self Distributor */}
        <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
          <h2 className="text-lg font-semibold mb-4">Self Distributor</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Self Distributor Enabled</label>
              <p className="text-sm text-gray-900">{formatValue(formData.addSelfDistributor)}</p>
            </div>
            {formData.addSelfDistributor && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Distributor ID</label>
                <p className="text-sm text-gray-900">{formatValue(formData.distributorId)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pb-8">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none transition-colors"
            disabled={principalLoading}
          >
            Back to Edit
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            disabled={principalLoading}
          >
            {principalLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {isEditMode ? "Updating..." : "Creating..."}
              </>
            ) : (
              isEditMode ? "Confirm Update" : "Confirm Create"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewPrincipalPage;
